import React from 'react';
import { Key, Building2, ArrowUpCircle, CheckCircle, Trash2 } from 'lucide-react';
import { UserProfile } from '@/shared/types';
import { Modal, Select, Button, Badge } from '@/shared/components/ui';

interface UserActionDialogProps {
  actionType: ActionType;
  actionUser: UserProfile | null;
  tempPassword: string | null;
  newPlanId: string;
  billingPeriod: string;
  onNewPlanIdChange: (value: string) => void;
  onBillingPeriodChange: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

export type ActionType = 'delete' | 'reset_password' | 'toggle_company' | 'change_plan' | null;

const PLAN_OPTIONS = [
  { value: 'free', label: 'Free Starter (Limits Applied)' },
  { value: 'basic', label: 'Basic (5 Clients)' },
  { value: 'pro', label: 'Pro Freelancer (Unlimited)' },
  { value: 'company', label: 'Company (Enterprise)' },
];

const BILLING_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

/**
 * UserActionDialog - Admin dialog for user actions
 * Migrated to use shared UI Modal, Select, Button, and Badge components
 */
export const UserActionDialog: React.FC<UserActionDialogProps> = ({
  actionType,
  actionUser,
  tempPassword,
  newPlanId,
  billingPeriod,
  onNewPlanIdChange,
  onBillingPeriodChange,
  onConfirm,
  onCancel,
}) => {
  if (!actionType || !actionUser) return null;

  const isDestructive = actionType === 'delete';
  const isReset = actionType === 'reset_password';
  const isPlan = actionType === 'change_plan';

  const getTitle = () => {
    switch (actionType) {
      case 'delete': return 'Delete Account';
      case 'reset_password': return 'Reset Password';
      case 'toggle_company': return 'Company Status';
      case 'change_plan': return 'Change Subscription';
    }
  };

  const getConfirmText = () => {
    if (actionType === 'delete') return 'Delete Account';
    if (actionType === 'change_plan') return 'Update Subscription';
    return 'Confirm Action';
  };

  return (
    <Modal
      isOpen={true}
      onClose={onCancel}
      title={getTitle()}
      size="md"
      footer={
        <div className="flex justify-end gap-3">
          {isReset && tempPassword ? (
            <Button variant="primary" onClick={onCancel}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                variant={isDestructive ? 'danger' : 'primary'}
                onClick={onConfirm}
                leftIcon={actionType === 'delete' ? <Trash2 size={14} /> : undefined}
              >
                {getConfirmText()}
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {isPlan ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Update subscription tier and billing cycle for <strong>{actionUser.name}</strong>.
            </p>
            <Select
              label="Package Plan"
              options={PLAN_OPTIONS}
              value={newPlanId}
              onChange={(e) => onNewPlanIdChange(e.target.value)}
            />
            <Select
              label="Billing Period"
              options={BILLING_OPTIONS}
              value={billingPeriod}
              onChange={(e) => onBillingPeriodChange(e.target.value)}
            />
          </div>
        ) : isReset && tempPassword ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 bg-success-100 text-success-600 rounded flex items-center justify-center mx-auto mb-3">
              <CheckCircle size={20} />
            </div>
            <h4 className="font-semibold text-gray-900 text-sm mb-1">Password Reset Successful</h4>
            <p className="text-gray-500 text-xs mb-4">Share this temporary password with the user.</p>
            <div className="bg-gray-100 p-3 rounded border border-gray-200 flex items-center justify-center gap-2">
              <span className="font-mono text-base font-semibold text-gray-900 tracking-wider select-all">{tempPassword}</span>
            </div>
          </div>
        ) : (
          <>
            <p className="text-gray-600 text-sm leading-relaxed">
              {actionType === 'delete' && <>Are you sure you want to permanently delete <strong>{actionUser.name}</strong>? This action cannot be undone and will remove all associated clients, jobs, and invoices.</>}
              {actionType === 'reset_password' && <>Generate a new temporary password for <strong>{actionUser.name}</strong>? They will be required to change it upon next login.</>}
              {actionType === 'toggle_company' && <>Toggle 'Company Account' status for <strong>{actionUser.name}</strong>? This usually grants free enterprise access.</>}
            </p>
            {actionType === 'toggle_company' && (
              <div className="bg-primary-50 p-3 rounded border border-primary-100 flex items-start gap-2">
                <Building2 className="text-primary-600 mt-0.5" size={14}/>
                <div className="text-xs text-primary-800">
                  <p className="font-medium">Current Status: <Badge variant="primary" size="xs">{actionUser.subscription.planId.toUpperCase()}</Badge></p>
                  <p className="opacity-80">Switching will update platform privileges immediately.</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};
